import TopicChip from '@/components/topic-chip/topic-chip';
import { Card, Flex, Form, Input } from 'antd';
import styles from './document-details.module.scss';
import { useDocument } from '@/hooks/use-document/use-document';
import { useParams } from 'react-router';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { UpdateDocumentPayload } from '@/services/documents-service/documents-service.types';
import { Button } from '@/components/button/button';
import clsx from 'clsx';
import { BackLink } from '@/components/back-link/back-link';
import { useTranslation } from 'react-i18next';

export function DocumentDetails() {
  const { t } = useTranslation(['documents', 'common']);
  const { documentId = '' } = useParams();
  const { documentData, updatedDocument, isPendingDocumentUpdate } = useDocument(documentId);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!isEditing) return;
    form.setFieldsValue({
      name: documentData?.name || '',
      content: documentData?.content || '',
    });
  }, [documentData, form, isEditing]);

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    form.resetFields();
    setIsEditing(false);
  };

  const handleFinish = (values: UpdateDocumentPayload) => {
    updatedDocument(
      { name: values.name, content: values.content },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      }
    );
  };

  return (
    <>
      <BackLink to="/documents" label={t('documents:details.backToList')} />
      <Flex vertical gap={16}>
        {!isEditing && (
          <Flex className={styles.editButtonWrapper} justify="end">
            <Button
              buttonVariant="primary"
              className={styles.editButton}
              onClick={handleEdit}
              disabled={documentData?.user_id != null}
            >
              <span className={styles.editButtonText}>{t('common:actions.edit')}</span>
            </Button>
          </Flex>
        )}

        <Card className={styles.detailsCard}>
          <div className={styles.chipWrapper}>
            <Flex align="center" gap={10}>
              <p className={styles.detailsLabel}>{t('documents:details.company')}</p>
              <TopicChip title={documentData?.company_name || '---'} />
            </Flex>

            {documentData?.user_id != null && (
              <Flex align="center" gap={10}>
                <p className={styles.detailsLabel}>{t('documents:details.userId')}</p>
                <TopicChip title={String(documentData.user_id)} />
              </Flex>
            )}

            <Flex align="center" gap={10}>
              <p className={styles.detailsLabel}>{t('documents:details.phoneNumber')}</p>
              <TopicChip title={documentData?.phone || t('common:states.none')} />
            </Flex>

            <Flex align="center" gap={10}>
              <p className={styles.detailsLabel}>{t('documents:details.lastModified')}</p>
              <p>
                {documentData?.updated_at
                  ? dayjs(documentData.updated_at).format('YYYY-MM-DD HH:mm:ss')
                  : '---'}
              </p>
            </Flex>

            <Flex align="center" gap={10}>
              <p className={styles.detailsLabel}>{t('documents:details.dateCreated')}</p>
              <p>
                {documentData?.created_at
                  ? dayjs(documentData.created_at).format('YYYY-MM-DD HH:mm:ss')
                  : '---'}
              </p>
            </Flex>
          </div>

          {isEditing ? (
            <Flex className={styles.formWrapper}>
              <Form
                className={styles.form}
                form={form}
                style={{ width: '100%' }}
                onFinish={handleFinish}
              >
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: t('documents:details.nameRequired') }]}
                  style={{ width: '100%', marginBottom: 0 }}
                >
                  <Input
                    aria-label={t('documents:details.nameLabel')}
                    className={styles.documentNameEditable}
                  />
                </Form.Item>

                <Form.Item name="content">
                  <Input.TextArea
                    aria-label={t('documents:details.contentLabel')}
                    className={styles.contentTextarea}
                    rows={10}
                    autoSize={{ minRows: 10, maxRows: 40 }}
                  />
                </Form.Item>
              </Form>
            </Flex>
          ) : (
            <Flex vertical gap={4} className={styles.contentWrapper}>
              <p className={styles.documentName}>{documentData?.name}</p>
              <div style={{ whiteSpace: 'pre-wrap' }} className={styles.detailsWrapper}>
                {documentData?.content}
              </div>
            </Flex>
          )}
        </Card>

        {isEditing && (
          <Flex gap={8} className={styles.editSaveButtons}>
            <Button
              buttonVariant="secondary"
              onClick={handleCancel}
              className={clsx(styles.editButton, styles.cancelButton)}
            >
              {t('common:actions.cancel')}
            </Button>
            <Button
              className={styles.editButton}
              buttonVariant="primary"
              onClick={() => form.submit()}
              loading={isPendingDocumentUpdate}
            >
              {t('common:actions.save')}
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
}
